import React, { lazy, Suspense, useEffect } from 'react';
import { Navigate, Outlet, Route, Routes, useLocation } from 'react-router';

import { observer } from 'mobx-react-lite';

import Header from './components/Header/Header';
import { Loader } from './components/UI/StateView/StateView';
import authStore from './store/auth';
import matchesStore from './store/matches';
import playerStore from './store/players';

import './App.scss';

// Страницы грузятся отдельными чанками
const ChangePasswordPage = lazy(
    () => import('./pages/ChangePasswordPage/ChangePasswordPage')
);
const HomePage = lazy(() => import('./pages/HomePage/HomePage'));
const HeroPage = lazy(() => import('./pages/HeroPage/HeroPage'));
const HeroesPage = lazy(() => import('./pages/HeroesPage/HeroesPage'));
const LoginPage = lazy(() => import('./pages/LoginPage/LoginPage'));
const MatchesPage = lazy(() => import('./pages/MatchesPage/MatchesPage'));
const MatchPage = lazy(() => import('./pages/MatchPage/MatchPage'));
const PlayerPage = lazy(() => import('./pages/PlayerPage/PlayerPage'));
const PlayersPage = lazy(() => import('./pages/PlayersPage/PlayersPage'));
const RandomizerPage = lazy(
    () => import('./pages/RandomizerPage/RandomizerPage')
);
const StatisticsPage = lazy(() =>
    import('./pages/StatisticsPage/StatisticsPage').then((m) => ({
        default: m.StatisticsPage,
    }))
);
const NotFoundPage = lazy(() => import('./pages/NotFoundPage/NotFoundPage'));

type ProtectedRouteProps = {
    children: JSX.Element;
    /** Только для владельца и админов */
    adminOnly?: boolean;
};

const ProtectedRoute = observer(
    ({ children, adminOnly = false }: ProtectedRouteProps) => {
        const location = useLocation();

        if (!authStore.isAuthorized) {
            return (
                <Navigate
                    to='/login'
                    state={{ from: location.pathname }}
                    replace
                />
            );
        }
        if (adminOnly && !authStore.isAdmin) {
            // Сохранённый до появления ролей пользователь — ждём verify
            if (!authStore.user?.role) return <Loader />;
            return <Navigate to='/' replace />;
        }
        return children;
    }
);

const protectRoute = (element: JSX.Element, adminOnly = false) => {
    return <ProtectedRoute adminOnly={adminOnly}>{element}</ProtectedRoute>;
};

const Layout = observer(() => {
    const { pathname } = useLocation();

    useEffect(() => {
        authStore.verify();
        matchesStore.fetchHeroes();
        // Логины игроков нужны для ссылок /players/<логин> по всему сайту
        if (playerStore.fetchStatus === 'idle') playerStore.fetchPlayers();
    }, []);

    // С временным паролем сайт недоступен, пока пароль не сменён
    if (authStore.mustChangePassword && pathname !== '/change-password') {
        return <Navigate to='/change-password' replace />;
    }

    return (
        <>
            <Header />
            <div className='wrapper'>
                <Suspense fallback={<Loader />}>
                    <Outlet />
                </Suspense>
            </div>
        </>
    );
});

export default function App() {
    return (
        <div className='App'>
            <Routes>
                <Route path='/' element={<Layout />}>
                    <Route index element={<HomePage />} />
                    <Route
                        path='/randomizer'
                        element={protectRoute(<RandomizerPage />, true)}
                    />
                    <Route path='/matches' element={<MatchesPage />} />
                    <Route path='/matches/:id' element={<MatchPage />} />
                    <Route path='/statistics' element={<StatisticsPage />} />
                    <Route path='/players' element={<PlayersPage />} />
                    <Route path='/players/:id' element={<PlayerPage />} />
                    <Route path='/heroes' element={<HeroesPage />} />
                    <Route path='/heroes/:id' element={<HeroPage />} />
                    <Route path='/login' element={<LoginPage />} />
                    <Route
                        path='/change-password'
                        element={protectRoute(<ChangePasswordPage />)}
                    />
                    <Route path='*' element={<NotFoundPage />} />
                </Route>
            </Routes>
        </div>
    );
}
